import { useMemo, useState } from 'react';
import { Art } from '../components/Art';
import { Button, InlineNote, Sheet, sheetText } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { header_arrived } from '../art/art';
import { useApp } from '../lib/app';
import { payView, TIP_CHOICES } from '../lib/fare';
import { codeOf, friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { clock, km, rupees } from '../lib/format';
import { shortName } from '../lib/places';
import type { Ride } from '../lib/types';
import { words } from '../lib/vehicles';
import { useSoon } from './common';
import { MapLine, MapView } from '../components/MapView';
import { useLayout } from '../lib/layout';
import { routeLine } from '../lib/polyline';
import { useColours } from '../theme/usePalette';
import { DropSquare, MapPill, StartRing } from './mapBits';

const TAGS = ['SAFE_DRIVING', 'ON_TIME', 'CLEAN_VEHICLE', 'POLITE'] as const;

export function Arrived({ ride, onDone, onSaved }: { ride: Ride; onDone: () => void; onSaved: (r: Ride) => void }) {
  const { api } = useApp();
  const t = useT();
  const name = ride.driver?.firstName ?? t('common.yourDriverLower');
  const alreadyTipped = ride.tipPaise > 0;
  const [tip, setTip] = useState<number>(ride.tipPaise);
  const [stars, setStars] = useState(ride.myRating ?? 0);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [note, , say] = useSoon();
  const [busy, setBusy] = useState(false);
  const pay = payView(ride, tip);
  const tookMin = ride.startedAt && ride.completedAt ? Math.max(1, Math.round((Date.parse(ride.completedAt) - Date.parse(ride.startedAt)) / 60000)) : Math.round(ride.durationSeconds / 60);
  const onTime = ride.startedAt && ride.completedAt ? tookMin <= Math.round(ride.durationSeconds / 60) + 5 : false;
  const rated = ride.myRating != null;
  const L = useLayout();
  const pal = useColours('map');
  const route = useMemo(() => routeLine(ride.pickup, ride.drop, ride.routePolyline, 0.12, 48), [ride.pickup, ride.drop, ride.routePolyline]);

  async function done() {
    setBusy(true);
    try {
      if (tip > 0 && !alreadyTipped) {
        try {
          onSaved(await api.tip(ride.id, tip));
        } catch (e) {
          if (codeOf(e) !== 'ALREADY_TIPPED') throw e;
        }
      }
      if (stars > 0 && !rated) {
        try {
          await api.rate(ride.id, stars, tags, comment.trim() || undefined);
        } catch (e) {
          if (codeOf(e) !== 'ALREADY_RATED') throw e;
        }
      }
      onDone();
    } catch (e) {
      say(friendly(e, t), 'error');
    } finally {
      setBusy(false);
    }
  }

  const chipBase = { height: 44, padding: '0 12px', border: 0, borderRadius: 22, color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer' };

  return (
    <Screen name="arrived-pay-rate" label={t('done.label')}>
      {L.wide && (
        // tablet / desktop: the finished trip stays on the map beside the receipt
        <MapView label={t('done.label')} center={ride.drop} fit={{ points: [ride.pickup, ride.drop], padding: { top: 90, bottom: 70, left: 140, right: 70 }, maxZoom: 15 }}>
          <MapLine id="trip-done" coords={route} color={pal.route} width={4.1} casing={pal.routeCase} casingWidth={8.3} />
          <StartRing at={ride.pickup} />
          <DropSquare at={ride.drop} />
          <MapPill at={ride.drop} side="left">
            {shortName(ride.drop.address)}
          </MapPill>
        </MapView>
      )}
      <div className="zr-scroll">
        <Stage top={0} height={96}>
          <Art piece={header_arrived} />
        </Stage>
        <header style={{ position: 'absolute', left: 20, right: 20, top: 'calc(var(--sat) + 10px)', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setIssueOpen(true)} style={{ height: 44, padding: '0 4px', border: 0, background: 'transparent', color: 'var(--ink2)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            <Icon name="help" size={18} />
            {t('done.report')}
          </button>
        </header>

        <div className="zr-stagger" style={{ position: 'relative', margin: 'calc(var(--sat) + 60px) 20px 0', paddingBottom: 'calc(var(--sab) + 96px)' }}>
          <div style={{ fontSize: 11.5, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--acText)' }}>
            {shortName(ride.drop.address).toUpperCase()} · {ride.completedAt ? clock(ride.completedAt).toUpperCase() : ''}
          </div>
          <h1 style={{ margin: '8px 0 0', fontSize: 42, lineHeight: '44px', fontWeight: 900, letterSpacing: '-0.04em' }}>{t('done.title')}</h1>
          <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>
            {km(ride.distanceMeters, 1)} · {tookMin} {t('common.min')}{onTime ? ` · ${t('done.onTime')}` : ''}
          </p>

          <section aria-label={t('done.payment')} data-testid="payment" data-status={ride.paymentStatus ?? ''} style={{ position: 'relative', marginTop: 22, borderRadius: 24, background: 'var(--s1)' }}>
            <div style={{ padding: '18px 18px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div data-testid="pay-head" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, lineHeight: '18px', fontWeight: 700, color: pay.paid ? 'var(--acText)' : 'var(--ink2)' }}>
                    {pay.paid && <Icon name="check" size={15} sw={2.8} />}
                    {t(pay.headKey, { name })}
                  </div>
                  <div data-testid="pay-total" key={pay.totalPaise} className="zr-tick" style={{ marginTop: 2, fontSize: 54, lineHeight: '58px', fontWeight: 900, letterSpacing: '-0.045em', fontVariantNumeric: 'tabular-nums' }}>{rupees(pay.totalPaise)}</div>
                </div>
                {/* online payment (UPI / card) needs Razorpay keys: not shown until it is set up */}
              </div>
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 14, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
                <span>{t('done.fare')}</span>
                <span style={{ color: 'var(--ink)' }}>{rupees(pay.farePaise)}</span>
              </div>
              {pay.discountPaise > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
                  <span>{t('done.discount')}</span>
                  <span style={{ color: 'var(--acText)' }}>−{rupees(pay.discountPaise)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
                <span>{t('done.tipFor', { name })}</span>
                <span style={{ color: 'var(--ink)' }}>{pay.tipPaise > 0 ? rupees(pay.tipPaise) : '—'}</span>
              </div>

            </div>
            <div aria-hidden="true" style={{ position: 'relative', height: 0, margin: '0 18px', borderTop: '1.5px dashed var(--line2)' }}>
              <span style={{ position: 'absolute', left: -29, top: -11, width: 20, height: 20, borderRadius: 10, background: 'var(--bg)' }} />
              <span style={{ position: 'absolute', right: -29, top: -11, width: 20, height: 20, borderRadius: 10, background: 'var(--bg)' }} />
            </div>
            <div style={{ padding: '14px 18px 18px' }}>
              <div id="tip-label" style={{ fontSize: 13, lineHeight: '18px', fontWeight: 700, color: 'var(--ink2)' }}>
                {t(pay.tipLabelKey, { name, amount: rupees(pay.tipPaise) })}
              </div>
              <div role="radiogroup" aria-labelledby="tip-label" style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                {TIP_CHOICES.map((v) => {
                  const on = tip === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={alreadyTipped}
                      onClick={() => setTip(v)}
                      style={{ height: 44, border: 0, borderRadius: 14, transition: 'background-color .2s var(--ease), color .2s var(--ease), scale .12s var(--ease)', background: on ? 'var(--chipOn)' : 'var(--s2)', color: on ? 'var(--chipOnInk)' : 'var(--ink)', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', cursor: alreadyTipped ? 'default' : 'pointer', opacity: alreadyTipped && !on ? 0.5 : 1 }}
                    >
                      {v === 0 ? t('done.noTip') : rupees(v)}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
          <InlineNote note={note} />

          <section aria-label={t('done.rate', { name })} style={{ marginTop: 24 }}>
            <h2 style={{ margin: 0, fontSize: 17, lineHeight: '22px', fontWeight: 700 }}>{rated ? t('done.rated', { name, stars: ride.myRating ?? '' }) : t('done.rate', { name })}</h2>
            <div role="radiogroup" aria-label={t('done.ratingAria', { n: stars })} style={{ marginTop: 8, display: 'flex', gap: 4, marginLeft: -6 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={stars === n}
                  aria-label={t.n('done.stars', n)}
                  disabled={rated}
                  onClick={() => setStars(n)}
                  style={{ width: 48, height: 48, border: 0, padding: 0, background: 'transparent', color: n <= stars ? 'var(--star)' : 'var(--s3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: rated ? 'default' : 'pointer' }}
                >
                  <svg aria-hidden="true" width="34" height="34" viewBox="0 0 24 24" key={n <= stars ? 'on' : 'off'} style={{ animation: n <= stars ? `zr-popin .26s var(--ease-out) ${(n - 1) * 40}ms both` : undefined }}>
                    <path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.6L12 16.6l-5.1 2.6 1-5.6-4.1-4 5.7-.8z" fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                  </svg>
                </button>
              ))}
            </div>
            {!rated && (
              <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                {TAGS.map((tag) => {
                  const label = t(`tag.${tag}`, { noun: words(t, ride.vehicleType).noun });
                  const on = tags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setTags((x) => (on ? x.filter((y) => y !== tag) : [...x, tag]))}
                      style={on ? { ...chipBase, background: 'var(--acSoft)', boxShadow: 'inset 0 0 0 1.5px var(--tagLine)' } : { ...chipBase, background: 'var(--s1)' }}
                    >
                      {on && <Icon name="check" size={16} sw={2.6} style={{ color: 'var(--acText)' }} />}
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5, padding: '24px 20px calc(var(--sab) + 3px)', background: 'linear-gradient(to bottom, transparent, var(--bg) 22px)', pointerEvents: 'none' }}>
        <div style={{ pointerEvents: 'auto' }}>
          <Button busy={busy} onClick={() => void done()}>
            {t(pay.buttonKey, { amount: rupees(pay.totalPaise) })}
          </Button>
        </div>
      </div>

      <Sheet open={issueOpen} onClose={() => setIssueOpen(false)} title={t('done.report')}>
        <p style={sheetText}>{t('done.issueBody')}</p>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={500}
          rows={4}
          aria-label={t('done.issueAria')}
          placeholder={t('done.issueHint')}
          className="zr-field"
          style={{ marginTop: 14, height: 'auto', padding: 14, resize: 'none' }}
        />
        <Button onClick={() => setIssueOpen(false)} style={{ marginTop: 14 }}>
          {comment.trim() ? t('done.addToRating') : t('common.close')}
        </Button>
      </Sheet>
    </Screen>
  );
}
